import React from 'react';
import ServicePageTemplate from './ServicePageTemplate';
import { Repeat, Clock, Users, HeartPulse, ShieldCheck, MapPin } from 'lucide-react';

const RecurringMedicalTransportation = () => {
  const seo = {
    title: 'Recurring Medical Transportation Houston | Standing NEMT Schedules | MoveraCare',
    description: 'Recurring non-emergency medical transportation across Greater Houston. Reliable standing schedules for dialysis, therapy, rehabilitation, occupational medicine, and other ongoing care.',
    path: '/services/recurring-medical-transportation',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Recurring Medical Transportation',
      serviceType: 'Non-Emergency Medical Transportation',
      areaServed: 'Greater Houston, TX',
      provider: { '@type': 'MedicalBusiness', name: 'MoveraCare Mobility', telephone: '+1-832-925-2128', url: 'https://www.moveracaremobility.com' },
    },
  };

  return (
    <ServicePageTemplate
      testid="service-recurring"
      seo={seo}
      eyebrow="RECURRING TRANSPORTATION"
      heading="Standing Non-Emergency Transportation Schedules Built for Ongoing Care"
      intro="MoveraCare Mobility provides recurring wheelchair and ambulatory transportation for treatment programs, therapy schedules, dialysis, rehabilitation, and other ongoing healthcare appointments across Greater Houston."
      who={{
        title: 'For Patients, Facilities, and Coordinators With Standing Care Needs',
        bullets: [
          'Dialysis patients on 3-day-per-week schedules',
          'Physical therapy and rehabilitation program participants',
          'Workers’ Compensation-authorized recurring appointments',
          'Facility residents attending recurring outpatient care',
          'Case managers coordinating multi-week treatment plans',
          'Families arranging weekly medical transportation',
        ],
      }}
      included={{
        title: 'Recurring Transportation Includes',
        bullets: [
          'Standing schedules aligned to appointment days and times',
          'Wheelchair or ambulatory vehicle assignment based on mobility',
          'Consistent driver assignments when possible',
          'Coordination with facility scheduling teams',
          'Wait-and-return support when arranged in advance',
          'Trip documentation supporting authorization and billing workflows',
          'Ad-hoc trip requests within the recurring plan',
        ],
      }}
      howItWorks={{
        title: 'From Standing Schedule to Ongoing Reliability',
        steps: [
          { title: 'Schedule Setup', desc: 'Share patient details, mobility, addresses, appointment cadence, and any authorization.' },
          { title: 'Standing Plan', desc: 'MoveraCare configures a recurring schedule and confirms operational details.' },
          { title: 'Weekly Coordination', desc: 'Trips run on the recurring plan with clear dispatch communication.' },
          { title: 'Changes & Adjustments', desc: 'Coordinators can request schedule changes or add ad-hoc trips through dispatch.' },
        ],
      }}
      why={{
        title: 'Why Coordinators Choose MoveraCare for Recurring Trips',
        items: [
          { icon: Repeat, title: 'Recurring-First Operations', desc: 'Designed around standing schedules — not one-off trips.' },
          { icon: Clock, title: 'Punctuality Focus', desc: 'On-time performance for treatment attendance.' },
          { icon: HeartPulse, title: 'Wheelchair & Ambulatory', desc: 'Options based on passenger mobility.' },
          { icon: Users, title: 'Driver Assistance', desc: 'Drivers assist passengers during transport, across every trip.' },
          { icon: ShieldCheck, title: 'HIPAA-Conscious', desc: 'Professional handling of patient information.' },
          { icon: MapPin, title: 'Greater Houston Coverage', desc: 'Service across our verified operating area.' },
        ],
      }}
      faqs={[
        { question: 'What kinds of appointments are supported as recurring?', answer: 'Dialysis, physical and occupational therapy, rehabilitation, Workers’ Compensation authorized care, wound care, infusion, and other recurring outpatient treatment.' },
        { question: 'Can we adjust the recurring schedule?', answer: 'Yes. Contact dispatch to change appointment days, times, or destinations. Ad-hoc trips can also be added within a recurring plan.' },
        { question: 'Are the same drivers assigned each week?', answer: 'When possible, we assign consistent drivers to support familiarity and continuity for the passenger.' },
        { question: 'Do you support wheelchair passengers on recurring schedules?', answer: 'Yes. Wheelchair-accessible vehicles are available for recurring transportation.' },
      ]}
    />
  );
};

export default RecurringMedicalTransportation;
